import { zodResolver } from '@hookform/resolvers/zod'
import { X, ChevronDown, ChevronRight, Zap, Settings, Sparkles, User, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'

import { DateQuickSelect } from '@/components/ui/date-quick-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  useCreateIssue,
  useUpdateIssue,
  type Issue,
  type CreateIssueInput,
  type UpdateIssueInput,
  type IssueType,
  type IssuePriority,
  useInitializeWorkflow,
  type InitializeWorkflowInput
} from '@/hooks'
import { useProject } from '@/hooks'
import { cn } from '@/lib/utils'

import { IssueAssignmentPanel } from './issue-assignment-panel'
import { TeamMemberSelector } from './team-member-selector'
import { WorkflowConfigForm } from './workflow-config-form'

/**
 * 计算两个日期之间的天数（包含两端）
 */
function calculateDurationDays(startDate: string, endDate: string): number {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = end.getTime() - start.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  return Math.max(diffDays, 1)
}

/**
 * 任务模板定义
 * Requirements: 1.4, 1.5 - 常用任务类型模板
 */
interface TaskTemplate {
  id: string
  name: string
  icon: string
  type: IssueType
  priority: IssuePriority
  estimatedHours: number
  workflowConfig: Partial<InitializeWorkflowInput>
  description?: string
}

const TASK_TEMPLATES: TaskTemplate[] = [
  {
    id: 'bug-fix',
    name: 'Bug 修复',
    icon: '🐛',
    type: 'bug',
    priority: 'high',
    estimatedHours: 4,
    workflowConfig: {
      productDiscussionEnabled: false,
      uiDesignEnabled: false,
      developmentConfig: 'both',
    },
    description: '修复系统中的错误或异常行为',
  },
  {
    id: 'feature',
    name: '新功能',
    icon: '✨',
    type: 'story',
    priority: 'medium',
    estimatedHours: 16,
    workflowConfig: {
      productDiscussionEnabled: true,
      uiDesignEnabled: true,
      developmentConfig: 'both',
    },
    description: '开发新的功能特性',
  },
  {
    id: 'refactor',
    name: '代码重构',
    icon: '🔧',
    type: 'task',
    priority: 'medium',
    estimatedHours: 8,
    workflowConfig: {
      productDiscussionEnabled: false,
      uiDesignEnabled: false,
      developmentConfig: 'backend_only',
    },
    description: '改进代码结构和质量',
  },
  {
    id: 'documentation',
    name: '文档编写',
    icon: '📝',
    type: 'task',
    priority: 'low',
    estimatedHours: 2,
    workflowConfig: {
      productDiscussionEnabled: false,
      uiDesignEnabled: false,
      developmentConfig: 'none',
    },
    description: '编写或更新项目文档',
  },
]

/**
 * 渐进式披露层级
 * Requirements: 1.2 - 渐进式披露隐藏高级配置
 */
type DisclosureLevel = 'basic' | 'workflow' | 'advanced'

interface IssueFormProps {
  issue?: Issue
  projectId?: string
  parentId?: string
  onClose: () => void
  onSuccess?: () => void
}

const TYPE_OPTIONS: { value: IssueType; label: string }[] = [
  { value: 'epic', label: '史诗' },
  { value: 'story', label: '故事' },
  { value: 'task', label: '任务' },
  { value: 'bug', label: '缺陷' },
]

const PRIORITY_OPTIONS: { value: IssuePriority; label: string }[] = [
  { value: 'urgent', label: '紧急' },
  { value: 'high', label: '高' },
  { value: 'medium', label: '中' },
  { value: 'low', label: '低' },
]

// Zod Schemas

const workflowConfigSchema = z.object({
  productDiscussionEnabled: z.boolean(),
  uiDesignEnabled: z.boolean(),
  opsDeploymentEnabled: z.boolean(),
  developmentConfig: z.enum(['none', 'frontend_only', 'backend_only', 'both'] as const),
  discussionParticipants: z.array(z.string()).default([]),
})

const issueSchema = z.object({
  title: z.string().min(1, '请输入任务标题').max(200, '任务标题不能超过200个字符'),
  description: z.string().max(10000, '任务描述不能超过10000个字符').optional(),
  type: z.enum(['epic', 'story', 'task', 'bug'] as const),
  priority: z.enum(['urgent', 'high', 'medium', 'low'] as const),
  estimatedHours: z.string().optional().refine(val => {
    if (!val) return true;
    const num = parseInt(val, 10);
    return !isNaN(num) && num >= 0 && num <= 1000;
  }, '预估工时必须在0-1000之间'),
  startDate: z.string().optional(),
  dueDate: z.string().optional(),
  reporterId: z.string().optional(),
  workflowConfig: workflowConfigSchema,
}).superRefine((data, ctx) => {
  if (data.startDate && data.dueDate && data.startDate > data.dueDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "开始日期不能晚于截止日期",
      path: ["startDate"]
    });
  }
});

type IssueFormValues = z.infer<typeof issueSchema>

/**
 * 任务表单组件（创建/编辑）
 * 
 * 实现渐进式披露的任务创建体验：
 * - 基础层：标题、描述、快速模板
 * - 工作流层：工作流配置
 * - 高级层：详细时间、优先级等设置
 * 
 * Requirements: 1.1, 1.2, 1.4, 1.5, 1.7
 */
export function IssueForm({ issue, projectId: initialProjectId, parentId, onClose, onSuccess }: IssueFormProps) {
  const isEdit = !!issue
  const createMutation = useCreateIssue()
  const updateMutation = useUpdateIssue()
  const initializeWorkflowMutation = useInitializeWorkflow()

  // 获取项目信息以获取 teamId
  const { data: project } = useProject(initialProjectId ?? issue?.projectId ?? '')

  // 渐进式披露状态 (UI only state)
  const [disclosureLevel, setDisclosureLevel] = useState<DisclosureLevel>('basic')
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null)

  const form = useForm<IssueFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(issueSchema) as any,
    defaultValues: {
      title: issue?.title || '',
      description: issue?.description || '',
      type: (issue?.type as IssueType) || 'task',
      priority: (issue?.priority as IssuePriority) || 'medium',
      estimatedHours: issue?.estimatedHours?.toString() || '',
      startDate: issue?.startDate ? issue.startDate.split('T')[0] : '',
      dueDate: issue?.dueDate ? issue.dueDate.split('T')[0] : '',
      reporterId: '',
      workflowConfig: {
        productDiscussionEnabled: false,
        uiDesignEnabled: false,
        opsDeploymentEnabled: false,
        developmentConfig: 'both',
        discussionParticipants: [],
      }
    },
    mode: 'onChange'
  })

  // IMPORTANT: For resolver compatibility, explicitly cast resolver if precise type alignment is tricky
  // However, removing unused variables first.
  const { register, control, handleSubmit, watch, reset, formState: { errors } } = form

  // Watch values for duration calculation
  const startDate = watch('startDate')
  const dueDate = watch('dueDate')

  // 应用模板 - Requirements: 1.4, 1.5
  const applyTemplate = (template: TaskTemplate) => {
    setSelectedTemplate(template)

    // Merge template values with current form values
    const currentValues = form.getValues()
    reset({
      ...currentValues,
      type: template.type,
      priority: template.priority,
      estimatedHours: template.estimatedHours.toString(),
      description: (!currentValues.description && template.description) ? template.description : currentValues.description,
      workflowConfig: {
        ...currentValues.workflowConfig,
        ...template.workflowConfig,
        // Ensure array is at least empty if undefined in partial
        discussionParticipants: template.workflowConfig.discussionParticipants || currentValues.workflowConfig.discussionParticipants || []
      }
    })
  }

  // Smart defaults - Requirements: 1.1
  useEffect(() => {
    if (!isEdit && !selectedTemplate && initialProjectId) {
      // 简单的启发式默认值
      // 在实际应用中，这里可以从 LocalStorage 读取上次的选择，或者根据项目类型设置
      const smartDefaults = {
        priority: 'medium' as const,
        type: 'task' as const,
        estimatedHours: '4',
      }

      const currentValues = form.getValues()
      // Only set if user hasn't modified them yet (simple check implies initial load)
      if (!form.formState.isDirty) {
        reset({
          ...currentValues,
          ...smartDefaults
        })
      }
    }
  }, [isEdit, initialProjectId, selectedTemplate, reset, form, form.formState.isDirty])

  // 一键创建 - Requirements: 1.4
  const handleQuickCreate = () => {
    void handleSubmit(onSubmit)()
  }

  const onSubmit = (data: IssueFormValues) => {
    const commonData = {
      title: data.title,
      description: data.description || undefined,
      type: data.type,
      priority: data.priority,
      estimatedHours: data.estimatedHours ? parseInt(data.estimatedHours, 10) : undefined,
      startDate: data.startDate || undefined,
      dueDate: data.dueDate || undefined,
    }

    if (isEdit && issue) {
      const updateData: UpdateIssueInput = {
        ...commonData,
        estimatedHours: commonData.estimatedHours === undefined ? null : commonData.estimatedHours,
        startDate: commonData.startDate || null,
        dueDate: commonData.dueDate || null,
      }

      updateMutation.mutate(
        { id: issue.id, data: updateData },
        {
          onSuccess: () => {
            onSuccess?.()
            onClose()
          },
        }
      )
    } else {
      if (!initialProjectId) {
        // Should theoretically be blocked by typing but good as safeguard
        console.error('Project ID missing')
        return
      }

      const createData: CreateIssueInput = {
        ...commonData,
        projectId: initialProjectId,
        parentId,
        reporterId: data.reporterId || undefined,
      }

      createMutation.mutate(createData, {
        onSuccess: (newIssue) => {
          // 创建成功后初始化工作流
          if (newIssue?.id) {
            initializeWorkflowMutation.mutate(
              { issueId: newIssue.id, data: data.workflowConfig },
              {
                onSuccess: () => {
                  onSuccess?.()
                  onClose()
                },
                onError: () => {
                  // 工作流初始化失败不影响任务创建
                  onSuccess?.()
                  onClose()
                },
              }
            )
          } else {
            onSuccess?.()
            onClose()
          }
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending || initializeWorkflowMutation.isPending
  const mutationError = createMutation.error || updateMutation.error || initializeWorkflowMutation.error

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-background shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {isEdit ? '编辑任务' : '新建任务'}
            </h2>
            {selectedTemplate && (
              <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-md">
                <span className="text-sm">{selectedTemplate.icon}</span>
                <span className="text-xs text-primary font-medium">{selectedTemplate.name}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} type="button" className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="p-6">
            <div className="space-y-6">

              {/* 快速模板选择 - Requirements: 1.4, 1.5 */}
              {!isEdit && disclosureLevel === 'basic' && (
                <div className="space-y-3">
                  <label className="text-sm font-medium">快速创建</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TASK_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => applyTemplate(template)}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover:bg-muted/50',
                          selectedTemplate?.id === template.id && 'border-primary bg-primary/5'
                        )}
                      >
                        <span className="text-lg">{template.icon}</span>
                        <div>
                          <div className="text-sm font-medium">{template.name}</div>
                          <div className="text-xs text-muted-foreground">{template.estimatedHours}h</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedTemplate && (
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      {selectedTemplate.description}
                    </div>
                  )}
                </div>
              )}

              {/* 基础信息 */}
              <div className="space-y-4">
                {/* 任务标题 */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    任务标题 <span className="text-destructive">*</span>
                  </label>
                  <Input
                    {...register('title')}
                    placeholder="请输入任务标题"
                    className={cn(errors.title && 'border-destructive')}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                {/* 任务描述 */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">任务描述</label>
                  <Textarea
                    {...register('description')}
                    placeholder="请输入任务描述"
                    rows={3}
                    className={cn(errors.description && 'border-destructive')}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-destructive">{errors.description.message}</p>
                  )}
                </div>

                {/* 产品经理选择 - 仅创建时显示 */}
                {!isEdit && project?.teamId && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      <User className="inline h-4 w-4 mr-1" />
                      产品经理
                    </label>
                    <Controller
                      control={control}
                      name="reporterId"
                      render={({ field }) => (
                        <TeamMemberSelector
                          teamId={project.teamId}
                          mode="single"
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="选择产品经理（默认为当前用户）"
                          teamRole="product"
                          showWorkload={false}
                        />
                      )}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      产品经理将自动成为产品研讨、UI确认、产品验收阶段的审核人
                    </p>
                  </div>
                )}
              </div>

              {/* 渐进式披露控制 - Requirements: 1.2 */}
              {!isEdit && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDisclosureLevel(disclosureLevel === 'basic' ? 'workflow' : 'basic')}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {disclosureLevel === 'basic' ? (
                      <>
                        <ChevronRight className="h-4 w-4" />
                        <Settings className="h-4 w-4" />
                        配置工作流
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        <Settings className="h-4 w-4" />
                        收起工作流配置
                      </>
                    )}
                  </button>

                  {disclosureLevel !== 'advanced' && (
                    <button
                      type="button"
                      onClick={() => setDisclosureLevel('advanced')}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <Sparkles className="h-4 w-4" />
                      高级选项
                    </button>
                  )}
                </div>
              )}

              {/* 工作流配置层 - Requirements: 1.2 */}
              {!isEdit && disclosureLevel !== 'basic' && (
                <div className="border-t pt-4">
                  <Controller
                    control={control}
                    name="workflowConfig"
                    render={({ field }) => (
                      <WorkflowConfigForm
                        value={field.value}
                        onChange={(newValue) => field.onChange(newValue)}
                        disabled={isPending}
                      />
                    )}
                  />
                </div>
              )}

              {/* 高级选项层 - Requirements: 1.2 */}
              {(isEdit || disclosureLevel === 'advanced') && (
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    高级选项
                  </h3>

                  {/* 类型和优先级 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">类型</label>
                      <Controller
                        control={control}
                        name="type"
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="选择类型" />
                            </SelectTrigger>
                            <SelectContent>
                              {TYPE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">优先级</label>
                      <Controller
                        control={control}
                        name="priority"
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="选择优先级" />
                            </SelectTrigger>
                            <SelectContent>
                              {PRIORITY_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>

                  {/* 预估工时和持续时间 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">预估工时（小时）</label>
                      <Input
                        type="number"
                        {...register('estimatedHours')}
                        placeholder="0"
                        min="0"
                        max="1000"
                        className={cn(errors.estimatedHours && 'border-destructive')}
                      />
                      {errors.estimatedHours && (
                        <p className="mt-1 text-sm text-destructive">{errors.estimatedHours.message}</p>
                      )}
                    </div>
                    <div>
                      {/* 持续时间显示 */}
                      {startDate && dueDate && (
                        <div className="flex h-full items-end pb-2">
                          <span className="text-sm text-muted-foreground">
                            持续: {calculateDurationDays(startDate, dueDate)} 天
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 开始日期和截止日期 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">开始日期</label>
                      <Input
                        type="date"
                        {...register('startDate')}
                        className={cn(errors.startDate && 'border-destructive')}
                      />
                      <Controller
                        control={control}
                        name="startDate"
                        render={({ field }) => (
                          <DateQuickSelect
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="mt-2"
                            options={{
                              includeToday: true,
                              includeTomorrow: true,
                              includeNextMonday: true,
                            }}
                          />
                        )}
                      />
                      {errors.startDate && (
                        <p className="mt-1 text-sm text-destructive">{errors.startDate.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">截止日期</label>
                      <Input
                        type="date"
                        {...register('dueDate')}
                        className={cn(errors.dueDate && 'border-destructive')}
                      />
                      <Controller
                        control={control}
                        name="dueDate"
                        render={({ field }) => (
                          <DateQuickSelect
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="mt-2"
                            options={{
                              includeToday: true,
                              includeTomorrow: true,
                              includeNextMonday: true,
                              includeThisWeekEnd: true,
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 任务分配（仅编辑模式显示） */}
              {isEdit && issue && (
                <div className="border-t pt-4">
                  <IssueAssignmentPanel
                    issueId={issue.id}
                    projectId={issue.projectId}
                  />
                </div>
              )}

              {/* 错误提示 */}
              {mutationError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {mutationError.message}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center gap-3 border-t px-6 py-4 bg-background flex-shrink-0">
          <div className="flex items-center gap-2">
            {!isEdit && disclosureLevel === 'basic' && (
              <Button
                type="button"
                onClick={handleQuickCreate}
                disabled={isPending || !watch('title')?.trim()}
                className="flex items-center gap-2"
              >
                <Zap className="h-4 w-4" />
                快速创建
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit(onSubmit)()}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? '保存' : '创建'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
